import BlockIcon from '@mui/icons-material/Block';
import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * Inspector detail-drawer footer (AUT-125). The three actions are
 * conditionally rendered based on gates the parent computes:
 *
 *   - "Одобрить KYC" — gated on `canApproveKyc(kyc)` (architect 🟠 #1).
 *     Renders even when the inspector is in `idcheck_pending` etc. — the
 *     gate is the session state, not the FSM status.
 *   - "Деактивировать" — gated on `canSuspend(status)` (🟠 #2 — only when
 *     status === 'active').
 *   - "Восстановить" — gated on `canReinstate(status)` (🟠 #2 — only when
 *     status === 'suspended').
 *
 * All three are role + permission + audit gated server-side. Audit-note
 * collapses to the right.
 */
export interface InspectorDetailFooterProps {
  canApproveKyc: boolean;
  canSuspend: boolean;
  canReinstate: boolean;
  busy: boolean;
  onApproveKyc: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
}

export const InspectorDetailFooter = ({
  canApproveKyc,
  canSuspend,
  canReinstate,
  busy,
  onApproveKyc,
  onSuspend,
  onReactivate,
}: InspectorDetailFooterProps): ReactElement => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: '14px 24px',
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}
    >
      {canApproveKyc ? (
        <Button
          variant="contained"
          color="success"
          disabled={busy}
          onClick={onApproveKyc}
          startIcon={<CheckIcon />}
        >
          {t('inspectors.actions.approveKyc')}
        </Button>
      ) : null}
      {canSuspend ? (
        <Button
          variant="outlined"
          color="error"
          disabled={busy}
          onClick={onSuspend}
          startIcon={<BlockIcon />}
        >
          {t('inspectors.actions.deactivate')}
        </Button>
      ) : null}
      {canReinstate ? (
        <Button
          variant="contained"
          color="success"
          disabled={busy}
          onClick={onReactivate}
          startIcon={<RestartAltIcon />}
        >
          {t('inspectors.actions.reactivate')}
        </Button>
      ) : null}
      <Box
        sx={{
          ml: 'auto',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          fontSize: 11,
          color: tokens.text.tertiary,
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 12 }} />
        {t('inspectors.actions.auditNote')}
      </Box>
    </Box>
  );
};
